import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { initPagination, goToPage, setPageSize } from '../stateReducer/actions';
import { DataGridContext } from './context';
import { ArrowFirstIcon, ArrowLastIcon, ChevronLeftBoldIcon, ChevronRightBoldIcon } from '@pattern-library/icon';
const propTypes = {
  /**
   * Additional classes to be added to the container
   */
  className: PropTypes.string,
  /**
   * The default number of rows to show per page.
   */
  defaultRowsPerPage: PropTypes.number,
  /**
   * The maximum number of page numbers to show. Not responsive.
   */
  maxPagesToShow: PropTypes.number,
  /**
   * An array of maxPagesToShow at the various breakpoints.
   * Must include all breakpoints in the following format:
   *
   * ```
   *  [
   *      { breakpoint: 'xs', pageCount: 3 },
   *      { breakpoint: 'sm', pageCount: 5 },
   *      { breakpoint: 'md', pageCount: 7 },
   *      { breakpoint: 'lg', pageCount: 8 },
   *      { breakpoint: 'xl', pageCount: 9 }
   *  ]
   * ```
   */
  maxPagesToShowAtSize: PropTypes.array,
  /**
   * The name of the entity to be displayed in the pagination counter.
   */
  pluralEntityName: PropTypes.string,
  /**
   * An array of options for the user to select the number of rows to show per page.
   */
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  /**
   * Whether to show the page numbers or not.
   */
  showPageNumbers: PropTypes.bool
};
const defaultProps = {
  defaultRowsPerPage: 10,
  maxPagesToShow: 10,
  pluralEntityName: 'rows',
  rowsPerPageOptions: [5, 10, 25, 50],
  showPageNumbers: false
};
const breakpoints = [{
  breakpoint: 'xs',
  max: 427
}, {
  breakpoint: 'sm',
  max: 767
}, {
  breakpoint: 'md',
  max: 1023
}, {
  breakpoint: 'lg',
  max: 1279
}, {
  breakpoint: 'xl',
  max: Number.MAX_SAFE_INTEGER
}];
const RESPONSIVE_BTN_CLASSNAME_PREFIX = 'asc-pagination-button--';
export default class DataGridPagination extends Component {
  constructor() {
    super(...arguments);
    this.firstPageToDisplay = () => {
      const {
        pagination = this.defaultPaginationState
      } = this.context;
      const {
        maxPagesToShow,
        maxPagesToShowAtSize
      } = this.props;
      if (maxPagesToShowAtSize) {
        return maxPagesToShowAtSize.map(size => {
          return pagination.currentPage - pagination.currentPage % size.pageCount;
        });
      }
      return [pagination.currentPage - pagination.currentPage % maxPagesToShow];
    };
    this.lastPageToDisplay = () => {
      const {
        maxPagesToShow,
        maxPagesToShowAtSize
      } = this.props;
      const firstPageToDislay = this.firstPageToDisplay();
      if (maxPagesToShowAtSize) {
        return maxPagesToShowAtSize.map((size, index) => {
          return Math.min(this.lastPageNumber, firstPageToDislay[index] + size.pageCount - 1);
        });
      }
      return [Math.min(this.lastPageNumber, firstPageToDislay[0] + maxPagesToShow - 1)];
    };
    this.handleGoToPage = pageIndex => {
      const {
        dispatch
      } = this.context;
      dispatch(goToPage(pageIndex));
    };
    this.currentBreakpoint = width => {
      return breakpoints.findIndex(bp => width <= bp.max);
    };
    this.handleGoToPreviousBatch = (event, firstPageToDisplay) => {
      const {
        maxPagesToShowAtSize
      } = this.props;
      if (maxPagesToShowAtSize) {
        const bpIndex = this.currentBreakpoint(event.view.innerWidth);
        this.handleGoToPage(firstPageToDisplay[bpIndex] - 1);
      } else {
        this.handleGoToPage(firstPageToDisplay[0] - 1);
      }
    };
    this.handleGoToNextBatch = (event, lastPageToDisplay) => {
      const {
        maxPagesToShowAtSize
      } = this.props;
      if (maxPagesToShowAtSize) {
        const bpIndex = this.currentBreakpoint(event.view.innerWidth);
        this.handleGoToPage(lastPageToDisplay[bpIndex] + 1);
      } else {
        this.handleGoToPage(lastPageToDisplay[0] + 1);
      }
    };
    this.handleGoToFirstPage = () => {
      this.handleGoToPage(0);
    };
    this.handleGoToPreviousPage = () => {
      const {
        pagination
      } = this.context;
      this.handleGoToPage(pagination.currentPage - 1);
    };
    this.handleGoToNextPage = () => {
      const {
        pagination
      } = this.context;
      this.handleGoToPage(pagination.currentPage + 1);
    };
    this.handleGoToLastPage = () => {
      this.handleGoToPage(this.lastPageNumber);
    };
    this.handleChangeRowsPerPage = event => {
      const {
        dispatch
      } = this.context;
      dispatch(setPageSize(Number(event.target.value)));
    };
  }
  componentDidMount() {
    const {
      dispatch
    } = this.context;
    const {
      defaultRowsPerPage
    } = this.props;
    dispatch(initPagination({
      pageSize: defaultRowsPerPage,
      currentPage: 0
    }));
  }
  get defaultPaginationState() {
    return {
      pageSize: this.props.defaultRowsPerPage,
      currentPage: 0
    };
  }
  get lastPageNumber() {
    const {
      filteredRows,
      pagination = this.defaultPaginationState
    } = this.context;
    return Math.ceil(filteredRows.length / pagination.pageSize - 1);
  }
  get hasPreviousPage() {
    const {
      pagination = this.defaultPaginationState
    } = this.context;
    return pagination.currentPage > 0;
  }
  get hasNextPage() {
    const {
      pagination = this.defaultPaginationState
    } = this.context;
    return pagination.currentPage < this.lastPageNumber;
  }
  get indexOfFirstVisibleRow() {
    const {
      pagination = this.defaultPaginationState
    } = this.context;
    return pagination.pageSize * pagination.currentPage + 1;
  }
  get indexOfLastVisibleRow() {
    const {
      filteredRows,
      pagination = this.defaultPaginationState
    } = this.context;
    const pageEndRow = this.indexOfFirstVisibleRow + pagination.pageSize - 1;
    return Math.min(filteredRows.length, pageEndRow);
  }
  render() {
    const {
      filteredRows,
      pagination = this.defaultPaginationState,
      showEmpty
    } = this.context;
    const {
      className,
      pluralEntityName,
      rowsPerPageOptions,
      showPageNumbers,
      maxPagesToShowAtSize
    } = this.props;
    if (filteredRows.length === 0 && showEmpty) return null;
    const lastPageToDisplay = this.lastPageToDisplay();
    const firstPageToDisplay = this.firstPageToDisplay();
    let previousPagesClassNames;
    let nextPagesClassNames;
    let pagesToDisplay;
    if (maxPagesToShowAtSize) {
      const firstPage = Math.min(...firstPageToDisplay);
      const lastPage = Math.max(...lastPageToDisplay);
      pagesToDisplay = new Array(lastPage - firstPage + 1).fill(0).map((_, i) => {
        return {
          pageNumber: i + firstPage,
          className: 'asc-pagination-button--num'
        };
      });
      maxPagesToShowAtSize.forEach((size, index) => {
        previousPagesClassNames = cx(previousPagesClassNames, {
          [' ' + RESPONSIVE_BTN_CLASSNAME_PREFIX + size.breakpoint]: firstPageToDisplay[index] > 0
        });
        nextPagesClassNames = cx(nextPagesClassNames, {
          [' ' + RESPONSIVE_BTN_CLASSNAME_PREFIX + size.breakpoint]: lastPageToDisplay[index] < this.lastPageNumber
        });
        pagesToDisplay.forEach(page => {
          if (page.pageNumber >= firstPageToDisplay[index] && page.pageNumber <= lastPageToDisplay[index]) {
            page.className = page.className + ' ' + RESPONSIVE_BTN_CLASSNAME_PREFIX + size.breakpoint;
          }
        });
      });
    } else {
      pagesToDisplay = new Array(lastPageToDisplay[0] - firstPageToDisplay[0] + 1).fill(0).map((_, i) => {
        return {
          pageNumber: i + firstPageToDisplay[0],
          className: 'asc-pagination-button--num'
        };
      });
    }
    return /*#__PURE__*/React.createElement("nav", {
      className: cx('asc-grid-pagination', className),
      "aria-label": "pagination"
    }, /*#__PURE__*/React.createElement("div", {
      className: "asc-grid-pagination-options"
    }, /*#__PURE__*/React.createElement("label", {
      htmlFor: "rowsPerPage",
      className: "asc-form-label"
    }, pluralEntityName, " per page"), /*#__PURE__*/React.createElement("div", {
      className: "asc-form-select-container"
    }, /*#__PURE__*/React.createElement("select", {
      id: "rowsPerPage",
      className: "asc-form-select",
      value: pagination.pageSize,
      onChange: this.handleChangeRowsPerPage
    }, rowsPerPageOptions.map(option => /*#__PURE__*/React.createElement("option", {
      key: option,
      value: option
    }, option))))), /*#__PURE__*/React.createElement("div", {
      className: "asc-grid-pagination-numbers"
    }, !showPageNumbers && /*#__PURE__*/React.createElement("div", {
      className: "asc-grid-pagination-counter"
    }, /*#__PURE__*/React.createElement("p", null, "Page ", pagination.currentPage + 1, " of ", this.lastPageNumber + 1)), showPageNumbers && /*#__PURE__*/React.createElement("div", {
      className: "asc-grid-pagination-counter"
    }, /*#__PURE__*/React.createElement("p", null, this.indexOfFirstVisibleRow, " - ", this.indexOfLastVisibleRow, " of", ' ', filteredRows.length)), /*#__PURE__*/React.createElement("ul", {
      className: "asc-pagination"
    }, /*#__PURE__*/React.createElement("li", null, /*#__PURE__*/React.createElement("button", {
      className: "asc-pagination-button asc-pagination-button--first",
      type: "button",
      disabled: !this.hasPreviousPage,
      onClick: this.handleGoToFirstPage
    }, /*#__PURE__*/React.createElement(ArrowFirstIcon, {
      size: "xxs",
      color: "current"
    }), /*#__PURE__*/React.createElement("span", {
      className: "asc-acc-hidden"
    }, "First page"))), /*#__PURE__*/React.createElement("li", null, /*#__PURE__*/React.createElement("button", {
      className: "asc-pagination-button asc-pagination-button--prev",
      type: "button",
      disabled: !this.hasPreviousPage,
      onClick: this.handleGoToPreviousPage
    }, /*#__PURE__*/React.createElement(ChevronLeftBoldIcon, {
      size: "xxs",
      color: "current"
    }), /*#__PURE__*/React.createElement("span", {
      className: "asc-acc-hidden"
    }, "Previous page"))), (firstPageToDisplay[0] > 0 || previousPagesClassNames) && /*#__PURE__*/React.createElement("li", null, /*#__PURE__*/React.createElement("button", {
      className: 'asc-pagination-button ' + previousPagesClassNames,
      type: "button",
      onClick: e => this.handleGoToPreviousBatch(e, firstPageToDisplay)
    }, /*#__PURE__*/React.createElement("span", {
      className: "asc-acc-hidden"
    }, "Previous pages"), "\u2026")), showPageNumbers && pagesToDisplay.map(page => {
      const pageNumber = page.pageNumber + 1;
      const isCurrentPage = page.pageNumber === pagination.currentPage;
      return /*#__PURE__*/React.createElement("li", {
        key: pageNumber,
        className: cx(isCurrentPage && 'is-active')
      }, /*#__PURE__*/React.createElement("button", {
        className: 'asc-pagination-button ' + page.className,
        type: "button",
        "aria-current": isCurrentPage ? 'page' : null,
        "aria-label": isCurrentPage ? `Page ${pageNumber}` : `Go to page ${pageNumber}`,
        onClick: () => this.handleGoToPage(page.pageNumber)
      }, pageNumber));
    }), (lastPageToDisplay[0] < this.lastPageNumber || nextPagesClassNames) && /*#__PURE__*/React.createElement("li", null, /*#__PURE__*/React.createElement("button", {
      className: 'asc-pagination-button ' + nextPagesClassNames,
      type: "button",
      onClick: e => this.handleGoToNextBatch(e, lastPageToDisplay)
    }, /*#__PURE__*/React.createElement("span", {
      className: "asc-acc-hidden"
    }, "Next pages"), "\u2026")), /*#__PURE__*/React.createElement("li", null, /*#__PURE__*/React.createElement("button", {
      className: "asc-pagination-button asc-pagination-button--next",
      type: "button",
      disabled: !this.hasNextPage,
      onClick: this.handleGoToNextPage
    }, /*#__PURE__*/React.createElement(ChevronRightBoldIcon, {
      size: "xxs",
      color: "current"
    }), /*#__PURE__*/React.createElement("span", {
      className: "asc-acc-hidden"
    }, "Next page"))), /*#__PURE__*/React.createElement("li", null, /*#__PURE__*/React.createElement("button", {
      className: "asc-pagination-button asc-pagination-button--last",
      type: "button",
      disabled: !this.hasNextPage,
      onClick: this.handleGoToLastPage
    }, /*#__PURE__*/React.createElement(ArrowLastIcon, {
      size: "xxs",
      color: "current"
    }), /*#__PURE__*/React.createElement("span", {
      className: "asc-acc-hidden"
    }, "Last page"))))));
  }
}
DataGridPagination.propTypes = propTypes;
DataGridPagination.defaultProps = defaultProps;
DataGridPagination.contextType = DataGridContext;